import { isNil } from 'ramda'
import { days, months } from './helpers'
import { DateInput, FormattedDate } from './formatDate.types'

// use formatDate to format a date IN LOCAL TIME
/**
 * Formats a date according to the specified format string.
 *
 * @example
 *   // Returns "Jan 01, 2023"
 *   formatDate('MMM DD, YYYY', new Date(2023, 0, 1))
 *
 * @example
 *   // Returns "05:30 pm EST"
 *   formatDate('hh:mm a tz', new Date(2023, 0, 1, 17, 30))
 *
 * @param format - A string that contains format specifiers which are replaced
 *   with date and time values:
 *
 *   - `A` - AM/PM indicator (uppercase)
 *   - `a` - am/pm indicator (lowercase)
 *   - `YYYY` - 4-digit year (e.g., 2023)
 *   - `YY` - 2-digit year (e.g., 23)
 *   - `MMMM` - Full month name (e.g., January)
 *   - `MMM` - Abbreviated month name (e.g., Jan)
 *   - `MM` - 2-digit month (01-12)
 *   - `DDDD` - Full day name (e.g., Monday)
 *   - `DDD` - Abbreviated day name (e.g., Mon)
 *   - `DD` - 2-digit day of the month (01-31)
 *   - `DL` - Day of the month without leading zero
 *   - `HH` - 2-digit hour in 24-hour format (00-23)
 *   - `hh` - 2-digit hour in 12-hour format (01-12)
 *   - `mm` - 2-digit minutes (00-59)
 *   - `ss` - 2-digit seconds (00-59)
 *   - `tz` - Time zone abbreviation (e.g., EST, PST)
 *
 * @param date - The date to format. Can be a Date object, a timestamp number,
 *   or a date string.
 * @returns The formatted date string or null if the date is invalid or nil.
 */
export const formatDate = (format: string, date: DateInput): FormattedDate => {
  const d =
    typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  if (isNil(d)) return null
  if (isNaN(d.valueOf())) return null

  const yr = d.getFullYear() + ''
  const mo = d.getMonth()
  const mon = mo + 1 // for when 1 should represent January
  const dt = d.getDate()
  const dy = d.getDay()
  const hr = d.getHours()
  const hrs = hr > 12 ? hr - 12 : hr // for 12 hour time
  const mm = d.getMinutes()
  const ss = d.getSeconds()
  const timeZone = d
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2]

  // longer versions must come before shorter versions
  // so that YYYY doesn't turn into 2 YY's. Also do the
  // am/pm thing first because we don't want months or
  // days to have their a and A letters hijacked.
  return format
    .replace(/A/g, hr < 12 ? 'AM' : 'PM')
    .replace(/a/g, hr < 12 ? 'am' : 'pm')
    .replace(/YYYY/g, yr)
    .replace(/YY/g, yr.substr(2))
    .replace(/MMMM/g, months[mo])
    .replace(/MMM/g, months[mo].substr(0, 3))
    .replace(/MM/g, mon < 10 ? `0${mon}` : String(mon))
    .replace(/DDDD/g, days[dy])
    .replace(/DDD/g, days[dy].substr(0, 3))
    .replace(/DD/g, dt < 10 ? `0${dt}` : String(dt))
    .replace(/DL/g, String(dt)) // single D was fucking up December
    .replace(/HH/g, hr < 10 ? `0${hr}` : String(hr))
    .replace(/hh/g, hrs < 10 ? `0${hrs}` : String(hrs))
    .replace(/mm/g, mm < 10 ? `0${mm}` : String(mm))
    .replace(/ss/g, ss < 10 ? `0${ss}` : String(ss))
    .replace(/tz/g, timeZone)
}
